import { Injectable, Type } from '@angular/core';
import { BaseService } from '../models/base/base-service';
import { environment } from '../../environments/environment';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  ignoreElements,
  map,
  retry,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  fromEvent,
  interval,
  merge,
  NEVER,
  Observable,
  of,
  Subject,
  throwError,
  timer
} from 'rxjs';
import { ToastService } from './toast-service';
import { exists } from '../functions/exists';
import { StringifyUtils } from '../utils/stringify-utils';
import { ApiClient } from '../api/api-client';
import { Deserializable } from '../models/protocols/deserializable';
import { WebSocketReceiveMessage } from '../models/web-sockets/messages/receive/web-socket-receive-message';
import { WebSocketConnectionEstablished } from '../models/web-sockets/messages/receive/web-socket-connection-established';
import { WebSocketMessageContextConnectionManager } from '../models/web-sockets/contexts/web-socket-message-context-connection-manager';
import { WebSocketRespondToHealthCheck } from '../models/web-sockets/messages/send/web-socket-respond-to-health-check';
import { WebSocketPong } from '../models/web-sockets/messages/receive/web-socket-pong';
import { WebSocketPing } from '../models/web-sockets/messages/send/web-socket-ping';
import { WebSocketConnectionState } from '../models/web-sockets/web-socket-connection-state';
import { ConnectionStatus } from '../models/web-sockets/enums/connection-status';
import { WebSocketMessageContextResponse } from '../models/web-sockets/contexts/web-socket-message-context-response';
import { WebSocketReceiveData } from '../models/web-sockets/messages/receive/web-socket-receive-data';
import { WebSocketRequestDisplay } from '../models/web-sockets/messages/send/web-socket-request-display';
import type { WebSocketSendMessage } from '../models/web-sockets/messages/send/web-socket-send-message';
import type { WebSocketMessageContext } from '../models/web-sockets/contexts/web-socket-message-context';
import type { WebSocketSendRequestDataMessage } from '../models/web-sockets/messages/send/web-socket-send-request-data-message';
import { WebsocketClientType } from '../models/enum/shared/websocket-client-type';
import { WebSocketConnectionInfo } from '../models/web-sockets/messages/send/web-socket-connection-info';

type DataNeededToSendMessage = [WebSocketSubject<any>, WebSocketSendMessage, number];
type ConnectionMetadata = [string, WebsocketClientType];

/**
 * Provided by Logged In Scope. This means the WebSocketService will be created when a user logs in,
 * and destroyed when the user logs out. The WebSocket API is protected by authentication, so the user must be
 * logged in to use it.
 *
 * If a domain model uses getRelevantMessages, make sure it's created as soon as the user logs in, within
 * the DashboardComponent (life cycle is scoped to the users authenticated state), so that it doesn't miss any
 * messages coming in from the WebSocket.
 */
@Injectable({ providedIn: 'root' })
export class WebSocketService extends BaseService {
  constructor(
    private apiClient: ApiClient,
    private toastService: ToastService
  ) {
    super();
    this.startSendMessageQueue();
    this.respondToHealthChecks();
    this.consoleLogUnaccountedForMessageContexts();
    this.listenForNetworkEvents();
    this.watchForProlongedDisconnect();
  }

  // @ts-ignore
  private readonly appVersion = (require('package.json')?.version as string)?.replace(/-[a-zA-Z0-9]+/, '');

  /**
   * The number of times the WebSocket connection has been retried.
   * Resets to 0 when the connection is successfully established.
   */
  private retryConnectionCount = 0;

  /**
   * When a WebSocket is opened, the server sends a "Connection Established" message to the client.
   * The connection is considered dead if the message is not received within the specified time.
   */
  private waitNSecondsForConnectionEstablishedMessage = 5;

  /**
   * When data requests are made through the WebSocket, the server sends a "Request Acknowledged" message to the client.
   * The request will throw an error if the message is not received within the specified time.
   */
  private waitNSecondsForRequestAcknowledgedMessage = 5;

  /**
   * Wait n seconds for the server to respond with the actual data, else consider the request "Timed Out" and error.
   */
  private waitNSecondsForDataRequestResponseThenTimeOut = 90;

  /**
   * The client pings the server every n seconds to check if the connection is still alive.
   */
  private pingServerEveryNSeconds = 60 * 3;

  /**
   * The client waits x seconds for the server to respond to a ping with a pong. If the server takes longer than
   * n seconds to respond, the ping is considered a failure.
   */
  private waitNSecondsForPongResponse = 5;

  /**
   * If the server fails to respond with n consecutive pongs, the connection is considered dead.
   */
  private numberOfFailedConsecutivePingsBeforeConnectionIsConsideredDead = 3;

  /**
   * An enum representation of the WebSocket connection status.
   * The connection status can be: Connecting, Connected, Disconnected.
   */
  private readonly _socketConnectionStatus = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.Connecting);
  public readonly socketConnectionStatus$ = this._socketConnectionStatus.pipe(distinctUntilChanged());

  /**
   * WebSocketMessageContextConnectionManager.ConnectionEstablished is accounted for by default within socket$.
   * WebSocketMessageContextConnectionManager.Pong is accounted for by default within socket$.
   */
  private accountedForMessageContexts: WebSocketMessageContext[] = [
    WebSocketMessageContextConnectionManager.ConnectionEstablished,
    WebSocketMessageContextConnectionManager.Pong,
    WebSocketMessageContextResponse.RequestAcknowledged,
    WebSocketMessageContextResponse.RequestNotAcknowledged,
    WebSocketMessageContextResponse.RequestResponse
  ];

  /**
   * Calling .complete() on the WebSocketSubject will close the underlying WebSocket connection gracefully by
   * sending a Close Frame to the server. It also completes the observable sequence, so any subscribers to the
   * WebSocketSubject will receive a complete notification.
   */
  private _socket: WebSocketSubject<WebSocketSendMessage | WebSocketReceiveMessage> | null = null;

  private readonly online$ = fromEvent(window, 'online');
  private readonly offline$ = fromEvent(window, 'offline');

  private readonly _displayIdForWebSocket = new BehaviorSubject<string | null>(null);
  public readonly displayIdForWebSocket$ = this._displayIdForWebSocket.pipe(
    distinctUntilChanged(),
    tap(() => this.connectToLocationId(null)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  connectToDisplayId = (displayId: string): void => {
    this._menuIdForWebSocket.next(null);
    this._templateIdForWebSocket.next(null);
    this._displayIdForWebSocket.next(displayId);
  };

  private readonly _menuIdForWebSocket = new BehaviorSubject<string | null>(null);
  public readonly menuIdForWebSocket$ = this._menuIdForWebSocket.pipe(distinctUntilChanged());
  connectToMenuId = (mId: string): void => {
    this._displayIdForWebSocket.next(null);
    this._templateIdForWebSocket.next(null);
    this._menuIdForWebSocket.next(mId);
  };

  private readonly _templateIdForWebSocket = new BehaviorSubject<string | null>(null);
  public readonly templateIdForWebSocket$ = this._templateIdForWebSocket.pipe(distinctUntilChanged());
  connectToTemplateId = (mId: string): void => {
    this._displayIdForWebSocket.next(null);
    this._menuIdForWebSocket.next(null);
    this._templateIdForWebSocket.next(mId);
  };

  /**
   * Don't next zero into here. An entityId of 0 is handled within clientInfoForDisplay$, because the only time the
   * entityId should be 0 is when establishing a web socket connection for a display.
   */
  private readonly _locationIdForWebSocket = new BehaviorSubject<number | null>(null);
  public readonly locationIdForWebSocket$ = this._locationIdForWebSocket.pipe(distinctUntilChanged());
  connectToLocationId = (locationId: number): void => this._locationIdForWebSocket.next(locationId);

  private readonly clientInfoForDisplay$ = this.displayIdForWebSocket$.pipe(
    map(id => {
      return !id ? null : new WebSocketConnectionInfo(id, WebsocketClientType.Display, 0);
    })
  );

  private readonly clientInfoForMenu$ = combineLatest([this.menuIdForWebSocket$, this.locationIdForWebSocket$]).pipe(
    map(([id, locationId]) => {
      return !id || !Number.isFinite(locationId)
        ? null
        : new WebSocketConnectionInfo(id, WebsocketClientType.Menu, locationId);
    })
  );

  private readonly clientInfoForTemplate$ = combineLatest([
    this.templateIdForWebSocket$,
    this.locationIdForWebSocket$
  ]).pipe(
    map(([id, locationId]) => {
      return !id || !Number.isFinite(locationId)
        ? null
        : new WebSocketConnectionInfo(id, WebsocketClientType.Template, locationId);
    })
  );

  private clientInfoToBuildWebSocketConnection$ = merge(
    this.clientInfoForDisplay$,
    this.clientInfoForMenu$,
    this.clientInfoForTemplate$
  ).pipe(filter(clientInfo => exists(clientInfo)));

  private readonly _toggleWebSocketConnectionLogic = new Subject<void>();
  public readonly toggleWebSocketConnectionLogic$ = this._toggleWebSocketConnectionLogic as Observable<void>;

  /**
   * This assigns a connection number to each web socket connection. This is useful for understanding
   * connection established/closed console log messages. Without this, you might see a connection closed message
   * after a connection opened message, leading someone to think that the recently opened connection was closed,
   * when in fact it was a previous connection that was closed.
   */
  private connectionNumber = 0;

  /**
   * The web socket connection with automatic reconnection.
   * "Connection Established" and "Pong" messages are handled inside the socket$ observable.
   *
   * export interface WebSocketSubjectConfig<T> {
   *   url: string;
   *   // The protocol to use to connect
   *   protocol?: string | Array<string>;
   *   // deprecated will be removed in v8. Use {@link deserializer} instead.
   *   resultSelector?: (e: MessageEvent) => T;
   *   // A serializer used to create messages from passed values before the
   *   // messages are sent to the server. Defaults to JSON.stringify.
   *   serializer?: (value: T) => WebSocketMessage;
   *   // A deserializer used for messages arriving on the socket from the server. Defaults to JSON.parse.
   *   deserializer?: (e: MessageEvent) => T;
   *   // An Observer that watches when open events occur on the underlying web socket.
   *   openObserver?: NextObserver<Event>;
   *   // An Observer that watches when close events occur on the underlying web socket
   *   closeObserver?: NextObserver<CloseEvent>;
   *   // An Observer that watches when a close is about to occur due to unsubscription.
   *   closingObserver?: NextObserver<void>;
   *   // A WebSocket constructor to use. This is useful for situations like using a
   *   // WebSocket impl in Node (WebSocket is a DOM API), or for mocking a WebSocket for testing purposes
   *   WebSocketCtor?: { new (url: string, protocols?: string | string[]): WebSocket };
   *   // Sets the `binaryType` property of the underlying WebSocket.
   *   binaryType?: 'blob' | 'arraybuffer';
   * }
   *
   * If webSocketUnexpectedlyClosedEvent$ is called, then re-toggle the socket$ observable to see if we need to
   * reconnect. webSocketUnexpectedlyClosedEvent$'s are abstracted events, like network interruptions, the server
   * closes the connection, an underlying error in the WebSocket Protocol, etc. So we need to capture these closures
   * and re-establish the connection.
   *
   * Note: the web socket connection is dependent on a displayId. Therefore, the web socket is only opened when
   * viewing a display.
   */
  private readonly socket$ = this.toggleWebSocketConnectionLogic$
    .pipe(
      debounceTime(1000),
      startWith(undefined),
      switchMap(() => this.clientInfoToBuildWebSocketConnection$),
      tap(() => this.closeWebSocketConnect()),
      switchMap(clientInfoToBuildWebSocketConnection => {
        const disconnectedMsg = this.getWillNotEstablishConnectionMessage(
          clientInfoToBuildWebSocketConnection?.clientId
        );
        if (exists(disconnectedMsg)) {
          return this.getWebSocketAsDisconnected$(ConnectionStatus.Disconnected, disconnectedMsg);
        }
        this._socketConnectionStatus.next(ConnectionStatus.Connecting);
        this.connectionNumber += 1;
        this._socket = webSocket({
          url: this.buildSocketUrl(clientInfoToBuildWebSocketConnection),
          deserializer: this.deserializer,
          serializer: this.serializer,
          openObserver: this.webSocketAPIConnectionOpened(this.connectionNumber),
          closeObserver: this.webSocketAPIConnectionClosed(this.connectionNumber)
        });
        return merge(
          // we have to wait for the connection established message, because it lets us know the entityId (locationId)
          // which is needed for sending messages to the server
          this.buildWaitUntilConnectionIsEstablished$().pipe(switchMap(() => of(this._socket))),
          this.buildErrorIfServerDoesNotEstablishConnection$(),
          this.buildErrorIfServerDoesNotRespondToPings$()
        ).pipe(
          // when clientInfoToBuildWebSocketConnection$ changes, we null out the old socket to avoid sending messages
          // through it while the new one connects
          startWith(null)
        );
      })
    )
    .pipe(
      retry(this.failedToConnectTryAgain()),
      // WARNING: returning of(null) here COMPLETES the shared socket$ stream, which is permanent - once it
      // completes it can never re-emit, so all reconnection (including online/offline events) stops until a
      // full page reload. This is only safe because retry() above never throws (it retries forever), so this
      // catchError is effectively unreachable. Do NOT reintroduce a throw/attempt-cap in failedToConnectTryAgain
      // without also making this path re-arm instead of complete.
      catchError(error => {
        console.warn('Failed to connect to WebSocket:', error);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  private getWillNotEstablishConnectionMessage(clientId: string): string | null {
    switch (true) {
      case !clientId:
        return 'No clientId provided. Cannot establish WebSocket API connection.';
      case !navigator?.onLine:
        return 'Display is not connected to the internet. WebSocket API connection will not be established.';
    }
    return null;
  }

  private getWebSocketAsDisconnected$(connectionStatus: ConnectionStatus, disconnectedMsg: string): Observable<null> {
    console.warn(disconnectedMsg);
    this._socketConnectionStatus.next(connectionStatus);
    return of(null);
  }

  private closeWebSocketConnect(): void {
    const previousSocket = this._socket;
    this._socket = null;
    previousSocket?.complete();
    this._socketConnectionStatus.next(ConnectionStatus.Disconnected);
  }

  private buildErrorIfServerDoesNotEstablishConnection$(): Observable<never> {
    return this._socket.pipe(
      filter((msg): msg is WebSocketConnectionEstablished => msg instanceof WebSocketConnectionEstablished),
      take(1),
      // timeout will throw an error if the server doesn't respond with a connection established message
      timeout(this.waitNSecondsForConnectionEstablishedMessage * 1000),
      switchMap(connectionEstablished => {
        return connectionEstablished?.payload?.connectionState === WebSocketConnectionState.Connected
          ? of(this._socket)
          : throwError(() => 'WebSocket established, but has no backend connection.');
      }),
      catchError(() => throwError(() => 'Server failed to establish socket connection')),
      // suppress emissions while still allowing errors
      ignoreElements()
    );
  }

  private buildErrorIfServerDoesNotRespondToPings$(): Observable<never> {
    return interval(this.pingServerEveryNSeconds * 1000).pipe(
      withLatestFrom(this.locationIdForWebSocket$.notNull$()),
      switchMap(([_, locationId]) => {
        return of(true).pipe(
          tap(() => this.sendMessage(new WebSocketPing(locationId))),
          switchMap(() => this._socket),
          filter((msg): msg is WebSocketPong => msg instanceof WebSocketPong),
          take(1),
          // timeout will throw an error if the server doesn't respond with a pong
          timeout(this.waitNSecondsForPongResponse * 1000),
          // error will be caught N times before error is thrown up the chain
          retry(this.numberOfFailedConsecutivePingsBeforeConnectionIsConsideredDead - 1),
          switchMap(() => of(this._socket))
        );
      }),
      // suppress emissions while still allowing errors
      ignoreElements()
    );
  }

  private buildWaitUntilConnectionIsEstablished$(): Observable<WebSocketConnectionEstablished> {
    return this._socket.pipe(
      filter((msg): msg is WebSocketConnectionEstablished => msg instanceof WebSocketConnectionEstablished),
      tap(connectionEstablishedMsg => {
        // The connection is only truly established once the server confirms it, so reset the reconnect
        // counter here rather than on raw socket open (a socket can open but never get established).
        this.retryConnectionCount = 0;
        // the entityId is always the locationId in the context of the display app
        if (Number.isFinite(connectionEstablishedMsg?.entityId)) {
          this.connectToLocationId(connectionEstablishedMsg.entityId);
        }
      }),
      take(1)
    );
  }

  public readonly inboundMessages$ = this.socket$.pipe(
    switchMap(socket$ => socket$ || of(null)),
    shareReplay({ bufferSize: 1, refCount: true }),
    filter(message => exists(message))
  );

  private readonly _sendMessageThroughQueue = new Subject<WebSocketSendMessage>();
  private readonly sendMessageThroughQueue$ = this._sendMessageThroughQueue.pipe(
    filter(message => exists(message)),
    concatMap(message => {
      return combineLatest([this.socket$, this.locationIdForWebSocket$]).pipe(
        filter(([socket, locationId]) => exists(socket) && Number.isFinite(locationId)),
        take(1),
        map(([socket, locationId]) => [socket, message, locationId] as DataNeededToSendMessage)
      );
    })
  );
  sendMessage = (message: WebSocketSendMessage) => this._sendMessageThroughQueue.next(message);

  /**
   * The entity id is tracked within this service, and not added to messages beforehand. This is because the entity id
   * is not known until the connection is established, and the server sends a "Connection Established" message.
   * The entity id automatically added to the message when it is sent through the queue.
   */
  startSendMessageQueue(): void {
    this.sendMessageThroughQueue$.subscribeWhileAlive({
      owner: this,
      next: ([socket, message, locationId]) => {
        message?.setEntityId(locationId);
        if (message instanceof WebSocketRequestDisplay) {
          message?.addLocationIdToPayloadMetadata(locationId);
        }
        message?.messageWasSent();
        socket?.next(message);
      }
    });
  }

  /**
   * Header params are not supported by the browser WebSocket API, so we have to pass them as query parameters.
   *
   * LocationId can be "" or 0, and it won't have any impact on the API. We will fetch the locationId based on
   * the object where applicable (ie: displayId will lookup and return the locationId when not provided).
   */
  private buildSocketUrl(wsConnectionInfo: WebSocketConnectionInfo): string {
    return (
      `${environment.webSocketUrl}` +
      `?ClientType=${wsConnectionInfo?.clientType}` +
      `&ClientId=${wsConnectionInfo?.clientId}` +
      `&EntityId=${wsConnectionInfo?.locationId}` +
      `&ClientVersion=${this.appVersion}` +
      `&source=Display`
    );
  }

  private deserializer = (e: MessageEvent<string>): WebSocketReceiveMessage => {
    return window?.injector?.Deserialize?.instanceOf(WebSocketReceiveMessage, JSON.parse(e?.data));
  };

  private serializer = (msg: any): any => JSON.stringify(msg?.onSerialize?.() ?? msg, StringifyUtils.replacer);

  private webSocketAPIConnectionOpened(connectionNumber: number): { next: () => void } {
    return {
      next: () => {
        console.info(`WebSocket API connection ${connectionNumber} opened`);
        this._socketConnectionStatus.next(ConnectionStatus.Connected);
      }
    };
  }

  /**
   * This event is triggered by the WebSocketSubject when the underlying WebSocket connection is closed.
   * This can happen for a variety of reasons, including network interruptions, server-side errors,
   * or the server closing the connection.
   */
  private webSocketAPIConnectionClosed(connectionNumber: number): { next: (closeEvent: CloseEvent) => void } {
    return {
      next: (closeEvent: CloseEvent) => {
        const reason = this.getWebSocketCloseReason(closeEvent?.code);
        console.info(`WebSocket API connection ${connectionNumber} closed${reason ? ': ' + reason : ''}`);
        if (reason) {
          console.info(closeEvent);
        }
        // We null this._socket whenever we tear a socket down ourselves (rebuild/offline/logout), so a
        // non-null value here means this close was NOT initiated by us and should reconnect. The
        // connectionNumber guard ignores superseded connections that a newer socket has already replaced.
        if (this._socket !== null && this.connectionNumber === connectionNumber) {
          this._toggleWebSocketConnectionLogic.next();
        }
      }
    };
  }

  /**
   * @param baseReconnectIntervalMs the delay before the first retry, doubled on each subsequent failure
   * @param maxReconnectIntervalMs the largest delay the backoff will grow to
   */
  private failedToConnectTryAgain(
    baseReconnectIntervalMs: number = 5000,
    maxReconnectIntervalMs: number = 300000
  ): { delay: (error: any) => any } {
    return {
      delay: error => {
        this.retryConnectionCount++;
        // 2 ** (n-1) doubles the wait each retry: 5s, 10s, 20s, 40s… clamped to maxReconnectIntervalMs (5min)
        // by Math.min. This backs off so an offline or flapping device keeps retrying without spamming the
        // server. retryConnectionCount resets once a connection is established.
        const backoffMs = Math.min(
          baseReconnectIntervalMs * 2 ** (this.retryConnectionCount - 1),
          maxReconnectIntervalMs
        );
        // After a few consecutive failures the connection is clearly down (not a transient blip), so surface
        // a single toast for visibility (~1 min in, given the backoff). We keep retrying with backoff after.
        if (this.retryConnectionCount === 5) {
          this.toastService.publishErrorMessage('Lost connection to the server, reconnecting…', 'WebSocket Error');
        }
        console.warn(
          `WebSocket error, attempting to reconnect (#${this.retryConnectionCount}) in ${backoffMs}ms:`,
          error
        );
        this._socketConnectionStatus.next(ConnectionStatus.Connecting);
        return timer(backoffMs);
      }
    };
  }

  private consoleLogUnaccountedForMessageContexts(): void {
    this.inboundMessages$
      .pipe(
        filter((message): message is WebSocketReceiveMessage => message instanceof WebSocketReceiveMessage),
        filter(message => exists(message) && !this.accountedForMessageContexts?.includes(message?.messageContext))
      )
      .subscribeWhileAlive({
        owner: this,
        next: message => console.warn('Unaccounted for WebSocket message context: ', message?.messageContext)
      });
  }

  private listenForNetworkEvents(): void {
    merge(this.offline$, this.online$).subscribeWhileAlive({
      owner: this,
      next: () => this._toggleWebSocketConnectionLogic.next()
    });
  }

  /**
   * Belt-and-suspenders for unattended displays: if the socket stays non-connected for this long, force a
   * fresh reconnect so a display can never sit silently offline waiting on a stalled reconnect.
   */
  private forceReconnectAfterDisconnectedMs = 60 * 60 * 1000;

  private watchForProlongedDisconnect(): void {
    this.socketConnectionStatus$
      .pipe(
        map(status => status === ConnectionStatus.Connected),
        distinctUntilChanged(),
        switchMap(isConnected => (isConnected ? NEVER : timer(this.forceReconnectAfterDisconnectedMs)))
      )
      .subscribeWhileAlive({
        owner: this,
        next: () => {
          console.warn('WebSocket has been disconnected too long; forcing a reconnect.');
          this._toggleWebSocketConnectionLogic.next();
        }
      });
  }

  private respondToHealthChecks(): void {
    this.getRelevantMessages$([WebSocketMessageContextConnectionManager.HealthCheck])
      .pipe(withLatestFrom(this.locationIdForWebSocket$.notNull$()))
      .subscribeWhileAlive({
        owner: this,
        next: ([_, locationId]) => this.sendMessage(new WebSocketRespondToHealthCheck(locationId))
      });
  }

  /**
   * @param relevantMessageContexts is required, if you pass in null or an empty array, the observable will never emit.
   */
  public getRelevantMessages$(relevantMessageContexts: WebSocketMessageContext[]): Observable<WebSocketReceiveMessage> {
    if (!relevantMessageContexts?.length) return NEVER;
    relevantMessageContexts?.forEach(context => this.accountedForMessageContexts.push(context));
    return this.inboundMessages$.pipe(
      filter((message): message is WebSocketReceiveMessage => message instanceof WebSocketReceiveMessage),
      filter(message => exists(message) && relevantMessageContexts?.includes(message?.messageContext)),
      finalize(() => relevantMessageContexts?.forEach(context => this.accountedForMessageContexts.remove(context))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * This is wrapped in a defer statement to prevent the message from being sent through the WebSocket before the
   * pipeline is returned and being listened to. This is important, because it prevents messages from the server from
   * being missed if the pipeline is returned but not immediately listened to.
   */
  protected request$<T extends Deserializable>(
    request: WebSocketSendRequestDataMessage,
    ObjectType: Type<T>,
    dataStructure: 'Object' | 'Array'
  ): Observable<T | T[]> {
    return defer(() => {
      const requestReceived = new Subject<boolean>();
      // Only build these error pipelines once per request, so that we don't create incorrect errors from connections'
      // dropping and reconnecting while the request is being processed.
      // Note: they will not start until they have been subscribed to at least once.
      const ackError$ = this.buildErrorIfServerDoesNotAcknowledgeRequest$(request);
      const timeoutError$ = this.buildErrorIfRequestIsConsideredTimedOut$(requestReceived);
      // wait until the pipeline is being listened to before sending the request
      setTimeout(() => this.sendMessage(request), 1);
      return this.socket$.pipe(
        switchMap(socket => {
          if (!socket) return NEVER;
          return request.messageSent$.pipe(
            filter(wasSent => wasSent),
            take(1),
            // error pipelines do not start until the first switchMap is executed
            switchMap(() => merge(this.inboundMessages$, ackError$, timeoutError$))
          );
        }),
        filter((received): received is WebSocketReceiveData => received instanceof WebSocketReceiveData),
        filter(received => request?.getPayloadRequestId() === received?.getRequestId()),
        take(1),
        tap(() => requestReceived.next(true)),
        // the payload is a pre-signed URL to the data
        map(returnedMessage => returnedMessage?.payload?.payload),
        switchMap(payloadURL => {
          switch (dataStructure) {
            case 'Object':
              return this.apiClient.getObj$(ObjectType, payloadURL);
            case 'Array':
              return this.apiClient.getArr$(ObjectType, payloadURL);
            default:
              return of(null);
          }
        }),
        take(1)
      );
    });
  }

  /**
   * shareReplay(1) caches the latest emission and keeps the pipeline active after the first subscription.
   * This ensures the pipeline isn’t recreated on each use (e.g., in switchMap, concatMap, mergeMap),
   * which is crucial for handling web socket disconnects/reconnects during in-flight data requests.
   */
  protected buildErrorIfServerDoesNotAcknowledgeRequest$(request: WebSocketSendRequestDataMessage): Observable<never> {
    return this.inboundMessages$.pipe(
      filter((msg): msg is WebSocketReceiveMessage => {
        return (
          msg instanceof WebSocketReceiveMessage &&
          request?.getPayloadRequestId() === msg?.getRequestId() &&
          msg?.messageContext === WebSocketMessageContextResponse.RequestAcknowledged
        );
      }),
      // take(1) sends a source observable complete signal to shareReplay(1), which will cause it to unsubscribe
      take(1),
      // timeout will throw an error if the server doesn't respond with a request acknowledged message
      timeout(this.waitNSecondsForRequestAcknowledgedMessage * 1000),
      catchError(() => throwError(() => `Server failed to acknowledge data request`)),
      // suppress emissions while still allowing errors
      ignoreElements(),
      shareReplay(1)
    );
  }

  /**
   * shareReplay(1) caches the latest emission and keeps the pipeline active after the first subscription.
   * This ensures the pipeline isn’t recreated on each use (e.g., in switchMap, concatMap, mergeMap),
   * which is crucial for handling web socket disconnects/reconnects during in-flight data requests.
   */
  protected buildErrorIfRequestIsConsideredTimedOut$(requestReceived$: Observable<boolean>): Observable<never> {
    return requestReceived$.pipe(
      timeout({
        each: this.waitNSecondsForDataRequestResponseThenTimeOut * 1000,
        with: () => throwError(() => `Request timed out`)
      }),
      // take(1) sends a source observable complete signal to shareReplay(1), which will cause it to unsubscribe
      take(1),
      // suppress emissions while still allowing errors
      ignoreElements(),
      shareReplay(1)
    );
  }

  requestObject$<T extends Deserializable>(
    request: WebSocketSendRequestDataMessage,
    ObjectType: Type<T>
  ): Observable<T> {
    return this.request$(request, ObjectType, 'Object') as Observable<T>;
  }

  requestArray$<T extends Deserializable>(
    request: WebSocketSendRequestDataMessage,
    ObjectType: Type<T>
  ): Observable<T[]> {
    return this.request$(request, ObjectType, 'Array') as Observable<T[]>;
  }

  getWebSocketCloseReason(code: number): string {
    switch (code) {
      case 1000:
        return ''; // Normal Closure
      case 1001:
        return 'Going Away – server is going down, browser is navigating away, etc.';
      case 1002:
        return 'Protocol Error – something went wrong at the protocol level.';
      case 1003:
        return "Unsupported Data – received data of a type it can't handle.";
      case 1005:
        return ''; // No Status Received (reserved)
      case 1006:
        return 'Abnormal Closure (reserved) – abnormal termination (e.g., dropped connection).';
      case 1007:
        return 'Invalid Data – data not consistent with the message type.';
      case 1008:
        return 'Policy Violation – message violates policy (e.g., too big).';
      case 1009:
        return "Message Too Big – can't process due to size.";
      case 1010:
        return 'Missing Extension – client expected a server extension.';
      case 1011:
        return 'Internal Error – unexpected condition prevented fulfillment.';
      case 1012:
        return 'Service Restart (unofficial).';
      case 1013:
        return 'Try Again Later (unofficial).';
      case 1014:
        return 'Bad Gateway (unofficial, used by some proxies).';
      case 1015:
        return 'TLS Handshake Failure (reserved).';
      default:
        if (code >= 3000 && code <= 4999) {
          return 'Application-defined close code.';
        }
        return 'Unknown or invalid close code.';
    }
  }

  override destroy() {
    super.destroy();
    this._socket?.complete();
  }
}
